import type {ProjectResponse, SelectOption} from 'src/api/ProactApiClient';
import type {ISelectOption} from 'src/types/Form';
import type {IFormDataV2, IFormFieldV2} from 'src/types/FormV2';
import {
  COUNTRY_OPTIONS,
  TIMEZONE_OPTIONS,
} from 'src/shared/constants/geoOptions';

export {COUNTRY_OPTIONS, TIMEZONE_OPTIONS};

export const ProjectDocumentTitle = 'Project';

export const MapProjectResponseToFormData = (
  resp: ProjectResponse
): IFormDataV2 => {
  return {
    id: resp.id,
    name: resp.name ?? '',
    code: resp.code ?? '',
    description: resp.description ?? '',
    address: resp.address ?? '',
    city: resp.city ?? '',
    state: resp.state ?? '',
    country: resp.country ?? '',
    postalCode: resp.postalCode ?? '',
    timeZone: resp.timeZone ?? '',
    active: resp.active ?? true,
    regionId: resp.regionId ?? '',
    oshaIndustryDescription: resp.oshaIndustryDescription ?? '',
    oshaSicCode: resp.oshaSicCode ?? '',
    oshaNaicsCode: resp.oshaNaicsCode ?? '',
  };
};

export const GetDefaultProjectFormData = (): IFormDataV2 => {
  return {
    name: '',
    code: '',
    description: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    timeZone: '',
    active: true,
    regionId: '',
    oshaIndustryDescription: '',
    oshaSicCode: '',
    oshaNaicsCode: '',
  };
};

const PROJECT_IDENTITY_FORM_META: IFormFieldV2[] = [
  {
    name: 'name',
    label: 'Name',
    type: 'text',
    required: true,
    size: 'md',
    validation: {maxLength: 255},
  },
  {
    name: 'code',
    label: 'Code',
    type: 'text',
    size: 'md',
    validation: {maxLength: 50},
  },
];

const PROJECT_DETAIL_FORM_META: IFormFieldV2[] = [
  {
    name: 'description',
    label: 'Description',
    type: 'textarea',
    validation: {maxLength: 1000},
    textboxOptions: {multiline: true},
  },
  {
    name: 'active',
    label: 'Active',
    type: 'switch',
    description:
      'Inactive projects are hidden from selection and use throughout the system.',
  },
];

export const ProjectGeneralFormMeta = (
  regions: SelectOption[]
): IFormFieldV2[] => [
  ...PROJECT_IDENTITY_FORM_META,
  {
    name: 'regionId',
    label: 'Region',
    type: 'select',
    size: 'md',
    required: false,
    options: regions as ISelectOption[],
  },
  ...PROJECT_DETAIL_FORM_META,
];

export const PROJECT_LOCATION_FORM_META: IFormFieldV2[] = [
  {
    name: 'address',
    label: 'Address',
    type: 'text',
    size: 'md',
    validation: {maxLength: 255},
  },
  {
    name: 'city',
    label: 'City',
    type: 'text',
    size: 'md',
    validation: {maxLength: 100},
  },
  {
    name: 'state',
    label: 'State / Province',
    type: 'text',
    size: 'md',
    validation: {maxLength: 100},
  },
  {
    name: 'country',
    label: 'Country',
    type: 'select',
    size: 'md',
    options: COUNTRY_OPTIONS,
  },
  {
    name: 'postalCode',
    label: 'Postal Code',
    type: 'text',
    size: 'md',
    validation: {maxLength: 20},
  },
  {
    name: 'timeZone',
    label: 'Time Zone',
    type: 'select',
    size: 'md',
    options: TIMEZONE_OPTIONS,
  },
];

export const PROJECT_OSHA_FORM_META: IFormFieldV2[] = [
  {
    name: 'oshaIndustryDescription',
    label: 'Industry Description',
    type: 'text',
    size: 'md',
    validation: {maxLength: 120},
  },
  {
    name: 'oshaSicCode',
    label: 'SIC Code',
    type: 'text',
    size: 'md',
    validation: {maxLength: 4},
  },
  {
    name: 'oshaNaicsCode',
    label: 'NAICS Code',
    type: 'text',
    size: 'md',
    validation: {maxLength: 6},
  },
];

// The generated request type is `number | undefined`; the select stores its
// value as a string and seeds '' when untouched.
export const MapProjectFormRegionId = (value: unknown): number | undefined =>
  value === '' || value === null || value === undefined
    ? undefined
    : Number(value);
